import React, { useMemo, useRef } from "react";

import { createBulletin } from "@rubi/api-generated";

import { BulletinRow, BulletinRowContext } from "./bulletinRowContext";

/**
 * Creates the bulletin row the media endpoints need — lazily, and **only ever from an upload**.
 *
 * Opening the form still creates nothing, which is problem 2 in the README's list of what the old
 * flow got wrong. Uploading a file is a different thing from reaching a step: the bytes have to be
 * stored somewhere, and that is what the officer asked for.
 *
 * It sits inside `BulletinFormProvider` so there is exactly one holder per form instance. Phase 2's
 * real `save` reads the same holder — a repository that created its own would leave the page
 * with two bulletins, one holding the media and one holding the fields.
 *
 * **In edit mode the row already exists**, and `existingId` is how it gets in. Without it the first
 * autosave of an edited bulletin would create a *second* row and write the officer's changes there,
 * leaving the one they opened untouched — the single worst thing phase 8 could get wrong, and
 * invisible until somebody looks at the drafts list.
 */
interface BulletinRowProviderProps {
  /** The bulletin this form is editing, when it is editing one. */
  existingId?: number;
  children: React.ReactNode;
}

export const BulletinRowProvider: React.FC<BulletinRowProviderProps> = ({
  existingId,
  children,
}) => {
  const id = useRef<number | null>(existingId ?? null);
  /** The in-flight create, shared so two files dropped at once do not make two bulletins. */
  const pending = useRef<Promise<number> | null>(null);

  const row = useMemo<BulletinRow>(
    () => ({
      id: () => id.current,
      ensureId: () => {
        if (id.current !== null) return Promise.resolve(id.current);
        if (pending.current === null) {
          pending.current = createBulletin({ body: {} })
            .then((response) => {
              if (!response.data) {
                throw response.error ?? new Error("Failed to create bulletin");
              }
              id.current = response.data.id;
              return response.data.id;
            })
            .catch((error) => {
              // Cleared so the next upload retries rather than resolving the failed promise again.
              pending.current = null;
              throw error;
            });
        }
        return pending.current;
      },
    }),
    [],
  );

  return (
    <BulletinRowContext.Provider value={row}>
      {children}
    </BulletinRowContext.Provider>
  );
};
